/* eslint-disable */

import React, {useRef} from 'react';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {useNavigate} from 'react-router-dom';
import {ResponsiveContainer} from 'recharts';
import ActivityGraph from '../components/Graph/ActivityGraph';
import {activitiesState, langState, categorySetState, setActivitiesState} from '../state/activities';
import {readAllFiles} from '../utils/readAllFiles';
import PlacesGraph from '../components/Graph/PlacesGraph';
import MoodGraph from '../components/Graph/MoodGraph';
import PainGraph from '../components/Graph/PainGraph';
import TogethernessGraph from '../components/Graph/TogethernessGraph';
import HVBCombinedGraph from '../components/HVBGraph/HVBCombinedGraph';
import UIButtonSmallRounded from '../components/UICommon/UIButtonSmallRounded';
import {default as text} from '../translate/lang.json'
import {Rulers} from '../components/Rulers/rulers';
import { SmallerRulers } from '../components/Rulers/shorterRulers';
import PageStatistics from './PageStatistics';
import HVBStatistics from '../components/HVBGraph/HVBStatistics';

const PageActivities = () => {
  const navigate = useNavigate();
  const [showResults, setShowResults] = React.useState(false)
  const onClick = () => setShowResults(showResults ? false : true)
  const activities = useRecoilValue(activitiesState);
  const lang = useRecoilValue(langState);
  const categorySet = useRecoilValue(categorySetState);
  const [activeTab, setActiveTab] = React.useState<'graph' | 'statistics'>('graph');

  const act: Array<any> = activities as Array<any>;

  // ─── HVB: inline file import ───
  const setActivities = useSetRecoilState(setActivitiesState);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = () => {
    if (!fileInputRef.current?.files?.length) return;
    const filesArray = Array.from(fileInputRef.current.files);
    readAllFiles(filesArray).then((result) => {
      setActivities(result);
    }).catch(() => alert('Error loading files.'));
    fileInputRef.current.value = ''; // reset so same file can be re-imported
  };

  // ─── HVB Layout ───
  if (categorySet === 'hvb') {
    return (
      <>
        {/* Hidden file input for inline import */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />

        {/* Compact header row: title + log pills + buttons + tabs all tight */}
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h1 className="text-black-accent text-2xl font-abel font-bold mr-2">
            {text.header[lang]}
          </h1>

          {/* Log entries info pills */}
          {act.map((value, index) => (
            <div
              key={index}
              className="flex items-center gap-1.5 border border-gray-accent rounded bg-white px-3 py-1 text-sm"
            >
              <span className="font-semibold text-black">{value.name}</span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">{value.date}</span>
            </div>
          ))}

          <div className="flex-grow" />

          {/* Action buttons */}
          <UIButtonSmallRounded
            text={text.import_button[lang]}
            handleClick={handleImportClick}
          />
          {activeTab === 'graph' && (
            <UIButtonSmallRounded
              text={text.rulers_button[lang]}
              handleClick={() => onClick()}
            />
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-300 mb-3">
          <button
            onClick={() => setActiveTab('graph')}
            className={`px-5 py-2 text-sm font-semibold transition-colors ${
              activeTab === 'graph'
                ? 'border-b-2 border-main-blue text-main-blue'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {text.tab_graph[lang]}
          </button>
          <button
            onClick={() => setActiveTab('statistics')}
            className={`px-5 py-2 text-sm font-semibold transition-colors ${
              activeTab === 'statistics'
                ? 'border-b-2 border-main-blue text-main-blue'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {text.tab_statistics[lang]}
          </button>
        </div>

        {/* Tab content */}
        {activeTab === 'graph' ? (
          <>
            {act.map((value, index) => (
              <div key={index} className="w-full mb-4">
                <HVBCombinedGraph arr={value} showRulers={showResults} />
              </div>
            ))}
          </>
        ) : (
          <HVBStatistics />
        )}
      </>
    );
  }

  // ─── Default Layout ───
  return (
    <>
      <h1 className="text-black-accent text-5xl font-abel mb-8">
        {text.header[lang]}
      </h1>
      <div className="flex mb-8">
        <UIButtonSmallRounded
          text={text.import_button[lang]}
          handleClick={() => navigate('/')}
        />
        <UIButtonSmallRounded
          text={text.stats_button[lang]}
          handleClick={() => navigate('/aktiviteter/statistik/')}
        />
        <UIButtonSmallRounded
          text={text.rulers_button[lang]}
          handleClick={() => onClick()}
        />
      </div>
      
      {act.map((value, index) => (
        <>
        <div className={`w-52 h-20 flex flex-col border-2 p-3 mb-8 ` +
        `border-gray-accent rounded-lg bg-white items-start`}>
          <div className="text-xl text-black font-bold">{value.name}</div>
          <div className="text-sm text-black">{value.date}</div>
        </div>
        <div
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 w-full relative`}
          >
            {showResults ? <Rulers /> : null}
            <div className={'relative min-h-[400px] flex items-stretch'}>
              <div className={'w-full'}>
                <ActivityGraph arr={value} />
              </div>
            </div>
            <div className={'relative min-h-[400px] flex items-stretch'}>
              <div className={'w-full'}>
                <PlacesGraph arr={value} />
              </div>
            </div>
            <div className={'relative min-h-[400px] flex items-stretch'}>
              <div className={'w-full'}>
                <TogethernessGraph arr={value} />
              </div>
            </div>
            <div className={'relative min-h-[400px] flex items-stretch'}>
              <div className={'w-full'}>
                {showResults ? <SmallerRulers /> : null}
                <MoodGraph arr={value} />
              </div>
            </div>
            <div className={'relative min-h-[400px] flex items-stretch'}>
              <div className={'w-full'}>
                <PainGraph arr={value} />
              </div>
            </div>
          </div>
        </>
        ))}
    </>
  );
};

export default PageActivities;
